'use strict';

/**
 * Moodboard-StickerSource
 * -----------------------
 * Adapts the plansheet sticker library to the StickerSource interface that pict-section-moodboard's
 * view expects (options.StickerSource). It merges three sets into one gallery:
 *   - the curated default stickers (ShapeSets), drawn from the bundled icon libraries, shown in the
 *     empty state (or the module's BUILTIN_STICKERS when no ShapesAPI is wired);
 *   - the rest of the icon libraries (Phosphor / Tabler / Brands), reachable by searching or by
 *     picking a Library filter, served recolored through ShapesAPI;
 *   - the customer's own uploaded stickers, read through StickerAPI.
 *
 * Uploads go through StickerAPI (admin-gated server-side; SVG is sanitized on store). Picking an
 * existing sticker just places it on the board, so add() is a no-op (it is already a URL).
 *
 * Interface used by the moodboard view (same contract as ImageSource):
 *   getFields() / getFilterOptions(key) / list(query) -> Promise<item[]> / upload(file, dataUrl, cb) / add(record)
 */

const libStickerResolver = require('../StickerSetResolver.js');

let _BUILTIN_STICKERS = [];
try { _BUILTIN_STICKERS = require('pict-section-moodboard/source/sources/StickerSource-Base.js').BUILTIN_STICKERS || []; }
catch (pErr) { _BUILTIN_STICKERS = []; }

// Library display label per ShapeLibrary collection key, and the reverse map for the gallery filter.
const _COLLECTION_LABEL = { phosphor: 'Phosphor', tabler: 'Tabler', simple: 'Brands' };
// The ShapeLibrary server-side recolor defaults; the sticker panel's color pickers start here so a freshly
// placed sticker shows its real colors before any override (phosphor pri=detail, sec=silhouette).
const _DEFAULT_PRI = '#1f3a52';
const _DEFAULT_SEC = '#9cc0e0';
const _LIBRARY_KEY = { 'Phosphor': 'phosphor', 'Tabler': 'tabler', 'Brands': 'simple' };

const _FIELDS =
[
	{ Key: 'Name', Label: 'Name', Type: 'string', Searchable: true, Sortable: true },
	{ Key: 'Collection', Label: 'Library', Type: 'enum', Filterable: true, Sortable: true }
];
const _COLLECTION_OPTIONS = ['Phosphor', 'Tabler', 'Brands', 'Uploads'];

class MoodboardStickerSource
{
	constructor(pOptions)
	{
		let tmpOptions = pOptions || {};
		this._StickerAPI = tmpOptions.StickerAPI || null;
		this._ShapesAPI = tmpOptions.ShapesAPI || null;
		// The bundled module built-ins, kept as the default set only when no ShapesAPI is wired.
		this._BuiltInItems = (Array.isArray(tmpOptions.BuiltInStickers) ? tmpOptions.BuiltInStickers : _BUILTIN_STICKERS).map((pSticker) =>
			({
				Id: String(pSticker.Id),
				Name: pSticker.Name || 'Sticker',
				Url: pSticker.Url,
				Thumbnail: pSticker.Url,
				Metadata: { Collection: 'Built-in', AddedAt: 0 }
			}));
		// The active sticker set is the gallery's empty state; everything else in the libraries is one
		// search away. The set is resolved per board context (drafting-design by default, or whatever
		// the host passes as SetKey / sets later via setActiveSet). Without a ShapesAPI we fall back to
		// the module's bundled built-ins, which carry their own data-URL art.
		this._SetItems = libStickerResolver.resolveStickerSet(tmpOptions.SetKey || libStickerResolver.DEFAULT_SET_KEY);
		this._DefaultItems = this._ShapesAPI
			? this._SetItems.map((pShape) => this._setItemToGalleryItem(pShape))
			: this._BuiltInItems;
	}

	// Swap the gallery's before-search palette to a different sticker set: a set key like 'moodboard',
	// or a raw items array from a custom set. Live search, uploads, and recolor are unaffected. A no-op
	// without a ShapesAPI (the bundled built-ins are the only drawable defaults there).
	setActiveSet(pSetKeyOrItems)
	{
		if (!this._ShapesAPI) { return; }
		this._SetItems = libStickerResolver.resolveStickerSet(pSetKeyOrItems);
		this._DefaultItems = this._SetItems.map((pShape) => this._setItemToGalleryItem(pShape));
	}

	// A resolved set item -> a gallery item. A library shape goes through _shapeItem (honoring any
	// per-item color override); an uploaded item ({IDSticker}) resolves to its stored blob.
	_setItemToGalleryItem(pItem)
	{
		if (pItem && pItem.IDSticker)
		{
			let tmpBlobURL = this._StickerAPI ? this._StickerAPI.blobURL(pItem.IDSticker) : '';
			return { Id: 'db-' + pItem.IDSticker, Name: pItem.Name || ('Sticker ' + pItem.IDSticker), Url: tmpBlobURL, Thumbnail: tmpBlobURL, Metadata: { Collection: 'Uploads', AddedAt: 0 } };
		}
		let tmpItem = this._shapeItem(pItem.Collection, pItem.Name, pItem.Title);
		if (pItem.Pri || pItem.Sec)
		{
			let tmpColors = { pri: pItem.Pri || _DEFAULT_PRI, sec: pItem.Sec || _DEFAULT_SEC };
			tmpItem.Url = tmpItem.Thumbnail = this.recolorURL(pItem.Collection, pItem.Name, tmpColors);
			if (pItem.Pri) { tmpItem.Metadata.ColorPrimary = pItem.Pri; }
			if (pItem.Sec) { tmpItem.Metadata.ColorSecondary = pItem.Sec; }
		}
		return tmpItem;
	}

	getFields() { return _FIELDS.map((pField) => Object.assign({}, pField)); }

	getFilterOptions(pKey) { return (pKey === 'Collection') ? _COLLECTION_OPTIONS.slice() : []; }

	list(pQuery)
	{
		let tmpQuery = pQuery || {};
		let tmpSearch = (tmpQuery.Search || '').trim();
		let tmpCollection = (tmpQuery.Filters && tmpQuery.Filters.Collection) || '';
		let tmpLibraryKey = _LIBRARY_KEY[tmpCollection] || '';
		// Reach into the icon index when a library is chosen, or for a real free-text search with no
		// library filter (the Uploads / Built-in filters stay local).
		let tmpWantRemote = !!this._ShapesAPI && (!!tmpLibraryKey || (tmpSearch.length >= 2 && !tmpCollection));
		let tmpRemote = tmpWantRemote
			? this._ShapesAPI.search(tmpSearch, tmpLibraryKey, 60)
				.then((pRows) => (Array.isArray(pRows) ? pRows : []).map((pRow) => this._shapeItem(pRow.Collection, pRow.Name, pRow.Title)))
				.catch(() => [])
			: Promise.resolve([]);
		return Promise.all([this._localItems(), tmpRemote]).then((pSets) =>
		{
			let tmpItems = this._dedup(pSets[0].concat(pSets[1]));
			return this._applyQuery(tmpItems, tmpQuery);
		});
	}

	upload(pFile, pDataUrl, fCallback)
	{
		if (!this._StickerAPI)
		{
			// No backend: place the cutout from its data URL so the drop is not lost.
			if (typeof fCallback === 'function') { fCallback(null, { Url: pDataUrl, Name: (pFile && pFile.name) || 'sticker' }); }
			return;
		}
		this._StickerAPI.upload(pFile, { MimeType: (pFile && pFile.type) || '' })
			.then((pRef) =>
			{
				let tmpID = pRef && pRef.IDSticker;
				if (typeof fCallback === 'function') { fCallback(null, { Url: this._StickerAPI.blobURL(tmpID), Id: 'db-' + tmpID, Name: (pFile && pFile.name) || 'sticker' }); }
			})
			.catch((pError) =>
			{
				// Upload is admin-gated; a non-admin (or a failure) gets nothing placed rather than an
				// orphan board reference to a blob that was never stored.
				if (typeof fCallback === 'function') { fCallback(pError || new Error('Sticker upload failed.')); }
			});
	}

	// Picking an existing sticker already references the library; nothing to register.
	add() { return null; }

	// A recolorable library shape. The node carries the shape identity + current colors so the sticker
	// panel can offer color overrides (phosphor is two-color / duotone; tabler + brands are single-color).
	_shapeItem(pCollection, pName, pTitle)
	{
		let tmpURL = this._ShapesAPI ? this._ShapesAPI.shapeURL(pCollection, pName) : '';
		return {
			Id: 'shape:' + pCollection + ':' + pName,
			Name: pTitle || pName,
			Url: tmpURL,
			Thumbnail: tmpURL,
			Metadata: {
				Collection: _COLLECTION_LABEL[pCollection] || 'Built-in', AddedAt: 0,
				ShapeCollection: pCollection, ShapeName: pName, Duotone: (pCollection === 'phosphor'),
				ColorPrimary: _DEFAULT_PRI, ColorSecondary: _DEFAULT_SEC
			}
		};
	}

	// A recolored URL for a library shape, rebuilt through ShapesAPI (which owns the pri/sec/op convention).
	// The moodboard calls this when a sticker's color override changes.
	recolorURL(pCollection, pName, pColors)
	{
		return this._ShapesAPI ? this._ShapesAPI.shapeURL(pCollection, pName, pColors || {}) : '';
	}

	_localItems()
	{
		if (!this._StickerAPI) { return Promise.resolve(this._DefaultItems.slice()); }
		return this._StickerAPI.listLibrary()
			.then((pRows) =>
			{
				let tmpUploads = (Array.isArray(pRows) ? pRows : []).filter((pRow) => !pRow.IsBuiltIn).map((pRow) => this._toItem(pRow));
				return this._DefaultItems.concat(tmpUploads);
			})
			.catch(() => this._DefaultItems.slice());
	}

	_toItem(pRow)
	{
		let tmpURL = this._StickerAPI ? this._StickerAPI.blobURL(pRow.IDSticker) : '';
		return {
			Id: 'db-' + pRow.IDSticker,
			Name: pRow.Name || ('Sticker ' + pRow.IDSticker),
			Url: tmpURL,
			Thumbnail: tmpURL,
			Metadata: { Collection: 'Uploads', AddedAt: pRow.SortOrder || 0 }
		};
	}

	_dedup(pItems)
	{
		let tmpSeen = {};
		let tmpOut = [];
		for (let i = 0; i < pItems.length; i++)
		{
			let tmpKey = pItems[i].Url || pItems[i].Id;
			if (tmpKey && tmpSeen[tmpKey]) { continue; }
			if (tmpKey) { tmpSeen[tmpKey] = true; }
			tmpOut.push(pItems[i]);
		}
		return tmpOut;
	}

	_fieldValue(pItem, pKey)
	{
		if (pKey === 'Name') { return pItem.Name; }
		return (pItem.Metadata && typeof pItem.Metadata[pKey] !== 'undefined') ? pItem.Metadata[pKey] : '';
	}

	_applyQuery(pItems, pQuery)
	{
		let tmpItems = pItems.slice();

		let tmpSearch = (pQuery.Search || '').trim().toLowerCase();
		if (tmpSearch)
		{
			tmpItems = tmpItems.filter((pItem) =>
			{
				let tmpHay = (pItem.Name + ' ' + this._fieldValue(pItem, 'Collection')).toLowerCase();
				return tmpHay.indexOf(tmpSearch) >= 0;
			});
		}

		let tmpFilters = pQuery.Filters || {};
		Object.keys(tmpFilters).forEach((pKey) =>
		{
			let tmpValue = tmpFilters[pKey];
			if (tmpValue === '' || tmpValue == null) { return; }
			tmpItems = tmpItems.filter((pItem) => String(this._fieldValue(pItem, pKey)) === String(tmpValue));
		});

		if (pQuery.Sort && pQuery.Sort.Field)
		{
			let tmpField = pQuery.Sort.Field;
			let tmpDir = (pQuery.Sort.Direction === 'desc') ? -1 : 1;
			tmpItems.sort((pA, pB) =>
			{
				let tmpAV = this._fieldValue(pA, tmpField);
				let tmpBV = this._fieldValue(pB, tmpField);
				if (tmpAV < tmpBV) { return -1 * tmpDir; }
				if (tmpAV > tmpBV) { return 1 * tmpDir; }
				return 0;
			});
		}

		return tmpItems;
	}
}

module.exports = MoodboardStickerSource;
